import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';
import z from 'zod';

import AngleUpIcon from '@/assets/icons/button-decorators/angle-up.svg?react';
import { DetailMapContentError } from '@/components/errors/DetailMapContentError';
import { DetailMapContentProviderWithPopup } from '@/components/map/DetailMapContentProviderWithPopup';
import { ServiceStationDetailMarker } from '@/components/map/ServiceStationDetailMarker';
import { ReportsList } from '@/components/report/ReportsList';
import { ServiceStationFeaturePopup } from '@/components/report/map/ServiceStationFeaturePopup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { ServiceStationFeatureDetails } from '@/components/service-station/ServiceStationFeatureDetails';
import { ServiceStationFeatureHeader } from '@/components/service-station/ServiceStationFeatureHeader';
import { useScreenSize } from '@/hooks/useScreenSize';
import { makeMetaTitle } from '@/modules/META';
import { useDuplicateReportsQuery } from '@/queries/useDuplicateReportsQuery';
import { serviceStationQueryOptions, useServiceStationQuery } from '@/queries/useServiceStationQuery';

const zValidateSearch = z.object({
  qr: z.boolean().optional().catch(undefined),
  details: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/_public/service-station/$serviceStationId')({
  validateSearch: zValidateSearch,

  loader: ({ params: { serviceStationId }, context: { queryClient } }) => {
    queryClient.ensureQueryData(serviceStationQueryOptions({ serviceStationId }));
  },

  head: async ({
    params: { serviceStationId },
    match: {
      context: { queryClient },
    },
  }) => {
    const serviceStation = await queryClient.ensureQueryData(serviceStationQueryOptions({ serviceStationId }));

    return {
      meta: [makeMetaTitle([serviceStation.properties.name])],
    };
  },

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const showDetails = search.details;
  const { isDesktop } = useScreenSize();

  const { data: serviceStationFeature, error } = useServiceStationQuery();

  const queryResult = useDuplicateReportsQuery({ serviceStationId: parseInt(params.serviceStationId, 10) });

  const buttons = [
    <ButtonLink
      key="create-report"
      search={{ servicestation: parseInt(params.serviceStationId, 10) }}
      startDecorator={<AngleUpIcon />}
      to="/"
    >
      Meldung erstellen
    </ButtonLink>,
  ];

  return (
    <Fragment>
      <DetailMapContentError error={error} />

      <ServiceStationDetailMarker />

      {serviceStationFeature ? (
        <DetailMapContentProviderWithPopup
          buttons={buttons}
          popup={<ServiceStationFeaturePopup feature={serviceStationFeature} />}
        >
          <ServiceStationFeatureHeader feature={serviceStationFeature} p={2} />

          {(showDetails || isDesktop) && params.serviceStationId ? (
            <Fragment>
              <ServiceStationFeatureDetails feature={serviceStationFeature} />
              <ReportsList
                noResultsText="Für diese Servicestation gibt es bisher keine Meldungen."
                queryResult={queryResult}
              />
            </Fragment>
          ) : null}
        </DetailMapContentProviderWithPopup>
      ) : null}
    </Fragment>
  );
}
